import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RegionFlag } from './RegionFlags';
import { SWITCHER_RADIUS } from './fieldStyles';
import { writeRegionCookie } from './regionCookie';
import { RegionDefinition, getRegions, regionForHost, regionSwitchUrl } from './regions';

/**
 * Region switcher for the login page (CNA-1902 / CNA-1906). Modelled on Datadog's
 * per-site login: picking a region navigates the browser to that region's own subdomain
 * and login page — it is a pre-authentication routing decision, not an in-session setting,
 * so there is no API call here and no auth state involved.
 *
 * A single dropdown button, same shape as the Genesys region picker on the HubSpot sign-in
 * screen (hubspot/index.tsx) — kept deliberately distinct from LanguageSwitcher's two-segment
 * ButtonGroup, which this used to copy and which does not scale past two regions.
 *
 * **Renders nothing unless the current host is itself a region.** Raised by Duy on CNA-1906:
 * previously this fell back to the default region on any unknown host, so on stage it showed
 * a switcher whose "Europe" entry navigated to `eu.wfm.cinareo.com` — production EU — taking
 * a tester clean out of the environment. `useRegionRedirect` already no-ops on those hosts by
 * design; this now matches it, so the two halves of the feature agree about what a region is.
 *
 * **And nothing unless at least two regions are configured.** Regions are opt-in (see
 * regions.ts): one with no configured domain is not in the registry, so an environment that
 * knows only its own region shows no switcher rather than a jump to a host that may not
 * answer yet.
 *
 * Consequence, and why regions.ts reads runtime config: under that rule the switcher is
 * invisible anywhere that is not a production region host, `localhost` included. An
 * environment that wants it visible says so by naming its own host in its runtime config —
 * which also keeps the other region pointing somewhere harmless.
 *
 * The label pairs the place with its AWS region — "Canada (ca-central-1)" — rather than the
 * identifier alone. The identifier matters here because the region is a data-residency
 * choice and customers ask where their data lives in exactly those terms, but on its own it
 * reads as jargon to anyone who is not an engineer. No `title` attribute: the visible text
 * already says everything a tooltip would repeat.
 */

export const RegionSwitcher = () => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const regions = getRegions();
  const currentRegion = regionForHost(globalThis.location?.host ?? '');
  // Not a region host — an environment. Render nothing rather than offering a jump that
  // would leave it. See the note above.
  if (!currentRegion) return null;
  // Only one region configured, so there is nothing to switch to. An environment turns the
  // switcher on by naming a second region's domain, never by the code shipping.
  if (regions.length < 2) return null;

  const handleSelect = (region: RegionDefinition) => {
    setAnchorEl(null);
    if (region.code === currentRegion.code) return;
    // Best-effort: persists on a real *.wfm.cinareo.com host, silently a no-op elsewhere
    // (see regionCookie.ts). Either way the redirect below still runs.
    writeRegionCookie(region.code);
    globalThis.location.href = regionSwitchUrl(region, globalThis.location);
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        aria-label="region selector"
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}
        startIcon={<RegionFlag code={currentRegion.code} />}
        endIcon={<FontAwesomeIcon icon={faChevronDown} size="xs" />}
        disableElevation
        // CNA-1969: same corner as the language switcher sitting beside it — see
        // SWITCHER_RADIUS for why the two share a value and why it is a string.
        sx={{ borderRadius: SWITCHER_RADIUS, textTransform: 'none', fontWeight: 600 }}
      >
        {t(currentRegion.labelKey)}
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        {regions.map((region) => (
          <MenuItem
            key={region.code}
            selected={region.code === currentRegion.code}
            onClick={() => handleSelect(region)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RegionFlag code={region.code} />
              {t(region.labelKey)}
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
